import { type SearchFilterAssetCustomFields } from '@air/api/types';

import { CustomFieldFilters, FilterParamNames } from '~/constants/search';
import {
  type CustomFieldSelectedFilter,
  type PlainTextCustomFieldFilter,
  type PlainTextCustomFieldOperator,
} from '~/store/filters/types';
import {
  dateCustomFieldAppliedCount,
  isCustomFieldDateInstant,
  isDateCustomFieldUrlParam,
  mergeAssetCustomFieldFilters,
  toAssetCustomFieldDateFilter,
} from '~/utils/filters/dateCustomFieldFilter';

export const PLAIN_TEXT_CUSTOM_FIELD_VALUE_MAX_LENGTH = 256;
export const SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES = 20;

export const PLAIN_TEXT_CUSTOM_FIELD_OPERATORS: readonly PlainTextCustomFieldOperator[] = [
  'is',
  'prefix',
  'or',
  'not',
  'notSet',
];

const plainTextCustomFieldOperators = new Set<string>(PLAIN_TEXT_CUSTOM_FIELD_OPERATORS);

export const PLAIN_TEXT_CUSTOM_FIELD_OPERATOR_LABELS: Record<PlainTextCustomFieldOperator, string> = {
  is: 'Is',
  prefix: 'Starts with',
  or: 'Is any of',
  not: 'Is not',
  notSet: 'Is empty',
};

export const PLAIN_TEXT_CUSTOM_FIELD_VALUE_TOO_LONG_MESSAGE = `Enter ${PLAIN_TEXT_CUSTOM_FIELD_VALUE_MAX_LENGTH} characters or fewer.`;
export const PLAIN_TEXT_CUSTOM_FIELD_TOO_MANY_VALUES_MESSAGE = `Add up to ${SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES} values.`;
export const PLAIN_TEXT_CUSTOM_FIELD_EMPTY_VALUE_MESSAGE = 'Enter a value.';

const PLAIN_TEXT_CUSTOM_FIELD_URL_PREFIXES = [
  FilterParamNames.cfTextIs,
  FilterParamNames.cfTextPrefix,
  FilterParamNames.cfTextOr,
  FilterParamNames.cfTextNot,
  FilterParamNames.cfNotSet,
];

export type PlainTextCustomFieldValueError = 'empty' | 'tooLong' | 'tooMany';

export const plainTextCustomFieldValueErrorMessage = (error: PlainTextCustomFieldValueError) => {
  switch (error) {
    case 'empty':
      return PLAIN_TEXT_CUSTOM_FIELD_EMPTY_VALUE_MESSAGE;
    case 'tooLong':
      return PLAIN_TEXT_CUSTOM_FIELD_VALUE_TOO_LONG_MESSAGE;
    case 'tooMany':
      return PLAIN_TEXT_CUSTOM_FIELD_TOO_MANY_VALUES_MESSAGE;
    default: {
      const exhaustiveError: never = error;

      return exhaustiveError;
    }
  }
};

export const isPlainTextCustomFieldOperator = (value: string): value is PlainTextCustomFieldOperator =>
  plainTextCustomFieldOperators.has(value);

export const normalizePlainTextCustomFieldValue = (value: string) => value.trim();

export const validatePlainTextCustomFieldValue = (value: string): PlainTextCustomFieldValueError | null => {
  const normalized = normalizePlainTextCustomFieldValue(value);

  if (!normalized) {
    return 'empty';
  }
  if (normalized.length > PLAIN_TEXT_CUSTOM_FIELD_VALUE_MAX_LENGTH) {
    return 'tooLong';
  }

  return null;
};

export const sanitizePlainTextCustomFieldValues = (values: string[]) => {
  const sanitized: string[] = [];
  values.forEach((value) => {
    if (sanitized.length >= SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES) {
      return;
    }
    if (validatePlainTextCustomFieldValue(value)) {
      return;
    }
    const normalized = normalizePlainTextCustomFieldValue(value);

    if (sanitized.includes(normalized)) {
      return;
    }
    sanitized.push(normalized);
  });
  return sanitized;
};

export const plainTextValuesForOperator = ({
  operator,
  values,
}: {
  operator: PlainTextCustomFieldOperator;
  values: string[];
}) => {
  if (operator === 'notSet') {
    return [];
  }
  const sanitized = sanitizePlainTextCustomFieldValues(values);

  if (operator === 'is' || operator === 'prefix') {
    return sanitized.slice(0, 1);
  }

  return sanitized;
};

export const toAssetCustomFieldTextFilter = (
  operator: PlainTextCustomFieldOperator,
  values: string[],
): SearchFilterAssetCustomFields['filter'] | null => {
  switch (operator) {
    case 'notSet':
      return { notSet: true };
    case 'is': {
      const [value] = sanitizePlainTextCustomFieldValues(values);

      if (!value) {
        return null;
      }

      return { text: { is: value } };
    }
    case 'prefix': {
      const [value] = sanitizePlainTextCustomFieldValues(values);

      if (!value) {
        return null;
      }

      return { text: { prefix: value } };
    }
    case 'or': {
      const sanitized = sanitizePlainTextCustomFieldValues(values);

      if (!sanitized.length) {
        return null;
      }

      return { text: { or: sanitized } };
    }
    case 'not': {
      const sanitized = sanitizePlainTextCustomFieldValues(values);

      if (!sanitized.length) {
        return null;
      }

      return { text: { not: sanitized } };
    }
    default: {
      const exhaustiveOperator: never = operator;

      return exhaustiveOperator;
    }
  }
};

export type PlainTextOperatorChangeResult =
  | { type: 'error'; error: 'tooLong' | 'tooMany' }
  | { type: 'commit'; plainText: PlainTextCustomFieldFilter; shouldApply: boolean };

export const resolvePlainTextOperatorChange = ({
  operator,
  values,
  nextOperator,
  editedValue,
}: {
  operator: PlainTextCustomFieldOperator;
  values: string[];
  nextOperator: PlainTextCustomFieldOperator;
  editedValue: string | undefined;
}): PlainTextOperatorChangeResult => {
  if (nextOperator === 'notSet') {
    return {
      type: 'commit',
      plainText: { operator: 'notSet', values: [] },
      shouldApply: true,
    };
  }

  const previousWasActive = toAssetCustomFieldTextFilter(operator, values) !== null;
  let sourceValues = values;

  if (editedValue !== undefined) {
    const validationError = validatePlainTextCustomFieldValue(editedValue);

    if (validationError === 'tooLong') {
      return { type: 'error', error: 'tooLong' };
    }

    const isMultiValueOperator = operator === 'or' || operator === 'not';

    if (validationError === 'empty') {
      sourceValues = isMultiValueOperator ? values : [];
    } else {
      const normalized = normalizePlainTextCustomFieldValue(editedValue);
      const nextIsSingleValue = nextOperator === 'is' || nextOperator === 'prefix';

      if (!isMultiValueOperator || nextIsSingleValue) {
        sourceValues = [normalized];
      } else if (values.includes(normalized)) {
        sourceValues = values;
      } else if (values.length >= SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES) {
        return { type: 'error', error: 'tooMany' };
      } else {
        sourceValues = [...values, normalized];
      }
    }
  }

  const nextValues = plainTextValuesForOperator({ operator: nextOperator, values: sourceValues });

  return {
    type: 'commit',
    plainText: { operator: nextOperator, values: nextValues },
    shouldApply: previousWasActive || toAssetCustomFieldTextFilter(nextOperator, nextValues) !== null,
  };
};

export const customFieldFilterIsActive = (field: CustomFieldSelectedFilter) => {
  if (field.date) {
    return toAssetCustomFieldDateFilter(field.date) !== null;
  }

  if (field.plainText) {
    return toAssetCustomFieldTextFilter(field.plainText.operator, field.plainText.values) !== null;
  }

  return field.values.length > 0;
};

export const customFieldUrlParamNames = (customFieldId: string) =>
  CustomFieldFilters.map((prefix) => `${prefix}${customFieldId}`);

export const isPlainTextCustomFieldUrlParam = (paramName: string) =>
  PLAIN_TEXT_CUSTOM_FIELD_URL_PREFIXES.some((prefix) => paramName.startsWith(prefix));

export const savedFilterCriteriaNeedsPlainTextCatalog = (
  filters: { customFields?: SearchFilterAssetCustomFields[] } | undefined,
) => {
  const customFields = filters?.customFields ?? [];

  return customFields.some((field) => {
    if ('text' in field.filter) {
      return true;
    }
    if ('date' in field.filter) {
      return true;
    }

    return field.filter.notSet === true;
  });
};

export const isCustomFieldFilterUrlParam = (paramName: string) =>
  CustomFieldFilters.some((prefix) => paramName.startsWith(prefix));

export const customFieldIdFromUrlParam = (paramName: string) => {
  const prefix = CustomFieldFilters.find((filterName) => paramName.startsWith(filterName));

  if (!prefix) {
    return;
  }
  const [, customFieldId] = paramName.split(prefix);

  return customFieldId;
};

const selectFilterFromUrlParam = ({
  id,
  logic,
  values,
}: {
  id: string;
  logic: 'and' | 'or' | 'not';
  values: string[];
}): SearchFilterAssetCustomFields | null => {
  if (!values.length) {
    return null;
  }
  switch (logic) {
    case 'and':
      return { id, filter: { and: values } };
    case 'or':
      return { id, filter: { or: values } };
    case 'not':
      return { id, filter: { not: values } };
    default: {
      const exhaustiveLogic: never = logic;

      return exhaustiveLogic;
    }
  }
};

export const assetCustomFieldFilterFromUrlParam = ({
  paramName,
  values,
}: {
  paramName: string;
  values: string[];
}): SearchFilterAssetCustomFields | null => {
  const id = customFieldIdFromUrlParam(paramName);

  if (!id) {
    return null;
  }

  if (paramName.startsWith(FilterParamNames.cfIdAnd)) {
    return selectFilterFromUrlParam({ id, logic: 'and', values });
  }
  if (paramName.startsWith(FilterParamNames.cfIdOr)) {
    return selectFilterFromUrlParam({ id, logic: 'or', values });
  }
  if (paramName.startsWith(FilterParamNames.cfIdNot)) {
    return selectFilterFromUrlParam({ id, logic: 'not', values });
  }
  if (paramName.startsWith(FilterParamNames.cfNotSet)) {
    return { id, filter: { notSet: true } };
  }

  if (paramName.startsWith(FilterParamNames.cfTextIs)) {
    const [value] = sanitizePlainTextCustomFieldValues(values);

    return value ? { id, filter: { text: { is: value } } } : null;
  }
  if (paramName.startsWith(FilterParamNames.cfTextPrefix)) {
    const [value] = sanitizePlainTextCustomFieldValues(values);

    return value ? { id, filter: { text: { prefix: value } } } : null;
  }
  if (paramName.startsWith(FilterParamNames.cfTextOr)) {
    const sanitized = sanitizePlainTextCustomFieldValues(values);

    return sanitized.length ? { id, filter: { text: { or: sanitized } } } : null;
  }
  if (paramName.startsWith(FilterParamNames.cfTextNot)) {
    const sanitized = sanitizePlainTextCustomFieldValues(values);

    return sanitized.length ? { id, filter: { text: { not: sanitized } } } : null;
  }
  if (paramName.startsWith(FilterParamNames.cfDateStart)) {
    const [value] = values;

    return value && isCustomFieldDateInstant(value) ? { id, filter: { date: { start: value } } } : null;
  }
  if (paramName.startsWith(FilterParamNames.cfDateEnd)) {
    const [value] = values;

    return value && isCustomFieldDateInstant(value) ? { id, filter: { date: { end: value } } } : null;
  }

  return null;
};

export const assetCustomFieldFiltersFromUrlParams = (customFields: { [key: string]: string[] }) =>
  mergeAssetCustomFieldFilters(
    Object.keys(customFields).flatMap((paramName) => {
      const values = customFields[paramName];

      if (!values) {
        return [];
      }

      const filter = assetCustomFieldFilterFromUrlParam({ paramName, values });

      return filter ? [filter] : [];
    }),
  );

export const customFieldUrlParamNeedsCatalog = (paramName: string) =>
  isPlainTextCustomFieldUrlParam(paramName) || isDateCustomFieldUrlParam(paramName);

type PlainTextFilterValue = {
  is?: string;
  prefix?: string;
  or?: string[];
  not?: string[];
};

export const plainTextStateFromTextFilter = (text: PlainTextFilterValue): PlainTextCustomFieldFilter | null => {
  if (typeof text.is === 'string') {
    const values = sanitizePlainTextCustomFieldValues([text.is]);

    return values.length ? { operator: 'is', values } : null;
  }
  if (typeof text.prefix === 'string') {
    const values = sanitizePlainTextCustomFieldValues([text.prefix]);

    return values.length ? { operator: 'prefix', values } : null;
  }
  if (text.or) {
    const values = sanitizePlainTextCustomFieldValues(text.or);

    return values.length ? { operator: 'or', values } : null;
  }
  if (text.not) {
    const values = sanitizePlainTextCustomFieldValues(text.not);

    return values.length ? { operator: 'not', values } : null;
  }

  return null;
};

const selectParamName = ({ id, logic }: { id: string; logic: CustomFieldSelectedFilter['logic'] }) => {
  switch (logic) {
    case 'and':
      return `${FilterParamNames.cfIdAnd}${id}`;
    case 'or':
      return `${FilterParamNames.cfIdOr}${id}`;
    case 'not':
      return `${FilterParamNames.cfIdNot}${id}`;
    default: {
      const exhaustiveLogic: never = logic;

      return exhaustiveLogic;
    }
  }
};

export const customFieldQueryEntry = (customField: CustomFieldSelectedFilter): [string, string[]] | null => {
  if (customField.date) {
    return null;
  }

  if (customField.plainText) {
    const filter = toAssetCustomFieldTextFilter(customField.plainText.operator, customField.plainText.values);

    if (!filter) {
      return null;
    }
    if ('notSet' in filter && filter.notSet) {
      return [`${FilterParamNames.cfNotSet}${customField.id}`, ['true']];
    }
    if ('text' in filter) {
      if (typeof filter.text.is === 'string') {
        return [`${FilterParamNames.cfTextIs}${customField.id}`, [filter.text.is]];
      }
      if (typeof filter.text.prefix === 'string') {
        return [`${FilterParamNames.cfTextPrefix}${customField.id}`, [filter.text.prefix]];
      }
      if (filter.text.or) {
        return [`${FilterParamNames.cfTextOr}${customField.id}`, filter.text.or];
      }
      if (filter.text.not) {
        return [`${FilterParamNames.cfTextNot}${customField.id}`, filter.text.not];
      }
    }

    return null;
  }

  if (!customField.values.length) {
    return null;
  }

  return [selectParamName({ id: customField.id, logic: customField.logic }), customField.values.map(({ id }) => id)];
};

export const getAssetCustomFieldAppliedCount = (filter?: SearchFilterAssetCustomFields['filter']) => {
  if (!filter) {
    return 0;
  }
  if ('text' in filter) {
    if (typeof filter.text.is === 'string' || typeof filter.text.prefix === 'string') {
      return 1;
    }

    return filter.text.or?.length ?? filter.text.not?.length ?? 0;
  }
  if ('date' in filter) {
    return dateCustomFieldAppliedCount(filter.date);
  }
  if (filter.notSet) {
    return 1;
  }

  return (filter.and?.length ?? 0) + (filter.or?.length ?? 0) + (filter.not?.length ?? 0);
};
